import {useCallback, useMemo, useSyncExternalStore} from 'react';
import {useNavigate} from 'react-router-dom';
import type {BackTarget} from 'src/shared/services/backTrail';
import {
  getBackTrailSnapshot,
  getTrackedRoutes,
  isCurrentEntryBackTarget,
  pageKeyForPath,
  resolveCurrentBack,
  subscribeBackTrail,
} from 'src/shared/services/backTrail';

export interface UseBackNavigationResult {
  goBack: () => void;
  /**
   * Exit to `to` after a save or transition: pops to the Back origin when it is that
   * page, else navigates, replacing the current entry when it is not a Back target.
   */
  returnTo: (to: string) => void;
  /** True when Back lands on the `fallbackTo` page (no origin, or the origin is that page). */
  returnsToFallbackPage: boolean;
  /** True when the Back origin is the page of any of `paths`. */
  originIs: (paths: string[]) => boolean;
}

/**
 * Back = the page the user came from (router back trail); `fallbackTo` is the
 * static parent used when the trail is empty (deep link, new tab).
 */
export const useBackNavigation = (
  fallbackTo: string
): UseBackNavigationResult => {
  const navigate = useNavigate();
  useSyncExternalStore(subscribeBackTrail, getBackTrailSnapshot);

  const routes = getTrackedRoutes();
  const fallbackPageKey = useMemo(
    () => pageKeyForPath(fallbackTo, routes),
    [fallbackTo, routes]
  );
  const target = resolveCurrentBack();
  const returnsToFallbackPage =
    !target || target.entry.pageKey === fallbackPageKey;

  const goToOrigin = useCallback(
    (origin: BackTarget | undefined, path: string) => {
      if (origin?.delta !== undefined) {
        navigate(origin.delta);
      } else {
        navigate(origin?.entry.path ?? path, {replace: true});
      }
    },
    [navigate]
  );

  const goBack = useCallback(
    () => goToOrigin(resolveCurrentBack(), fallbackTo),
    [goToOrigin, fallbackTo]
  );

  const returnTo = useCallback(
    (to: string) => {
      const origin = resolveCurrentBack();
      if (
        origin &&
        origin.entry.pageKey === pageKeyForPath(to, getTrackedRoutes())
      ) {
        goToOrigin(origin, to);
      } else {
        navigate(to, {replace: !isCurrentEntryBackTarget()});
      }
    },
    [goToOrigin, navigate]
  );

  const originIs = useCallback((paths: string[]) => {
    const origin = resolveCurrentBack();
    if (!origin) return false;
    const routeTable = getTrackedRoutes();
    return paths.some(
      path => pageKeyForPath(path, routeTable) === origin.entry.pageKey
    );
  }, []);

  return {goBack, returnTo, returnsToFallbackPage, originIs};
};
